import { Link, useParams } from 'react-router-dom';
import PageShell from '@/components/feature/PageShell';
import PageHero from '@/components/feature/PageHero';
import { courses, doctors, hospitalCourses, videos } from '@/mocks/home';
import { fetchDoctorInfo } from '@/services/doctor';
import { fetchCourseList } from '@/services/course';
import { fetchVideoList } from '@/services/video';
import { adaptDoctor, adaptCourses, adaptVideos } from '@/services/adapters';
import type { DoctorItem } from '@/types/content';
import { useRequest } from '@/hooks/useRequest';

export default function DoctorDetailPage() {
  const { id } = useParams();
  const doctorId = Number(id);

  // 医师详情（接口失败时降级为 mock 数据）
  const { data: remoteDoctor } = useRequest(
    () => fetchDoctorInfo(doctorId).then((res) => adaptDoctor(res.dataInfo)),
    { fallback: null as DoctorItem | null },
  );

  // 该医师的主讲课程
  const { data: remoteCourses } = useRequest(
    () => fetchCourseList({ pageIndex: 1, pageSize: 4, doctorId }).then((res) => adaptCourses(res.list)),
    { fallback: [] },
  );

  // 该医师的相关视频
  const { data: remoteVideos } = useRequest(
    () => fetchVideoList({ pageIndex: 1, pageSize: 3, doctorId }).then((res) => adaptVideos(res.list)),
    { fallback: [] },
  );

  const mockDoctor = doctors.find((item) => item.id === doctorId) ?? null;
  const doctor: DoctorItem | null = remoteDoctor ?? mockDoctor;

  if (!doctor) {
    return (
      <PageShell>
        <PageHero
          eyebrow="医师团队"
          title="医师信息不存在"
          description="抱歉，未找到该医师信息，可能链接有误。"
          breadcrumb={[{ label: '首页', to: '/' }, { label: '医师团队', to: '/doctors' }, { label: '医师主页' }]}
        />
        <section className="w-full px-4 md:px-8 py-16 text-center">
          <Link
            to="/doctors"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            返回医师团队
          </Link>
        </section>
      </PageShell>
    );
  }

  const allMockCourses = [...courses, ...hospitalCourses];
  const doctorCourses = remoteDoctor
    ? remoteCourses
    : allMockCourses.filter((item) => item.doctor === doctor.name).slice(0, 4);
  const doctorVideos = remoteDoctor
    ? remoteVideos
    : videos.filter((item) => item.doctor === doctor.name).slice(0, 3);

  return (
    <PageShell>
      <PageHero
        eyebrow="医师团队"
        title={doctor.name}
        breadcrumb={[{ label: '首页', to: '/' }, { label: '医师团队', to: '/doctors' }, { label: '医师主页' }]}
      />

      <section className="w-full px-4 md:px-8 py-12 md:py-16">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10">
          <div className="lg:col-span-2">
            <div className="flex flex-col sm:flex-row gap-6 items-start p-6 rounded-lg bg-background-100 border border-background-200">
              <div className="w-28 h-28 rounded-full overflow-hidden shrink-0 border-4 border-background-50">
                <img
                  src={doctor.avatar}
                  alt={doctor.name}
                  title={`${doctor.name} - ${doctor.title}`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">{doctor.name}</h2>
                <p className="mt-1 text-sm text-primary-600 font-medium">{doctor.title}</p>
                <p className="mt-1 text-sm text-foreground-600">{doctor.hospital}</p>
                <div className="flex flex-wrap items-center gap-2 mt-3">
                  {doctor.tags.map((tag) => (
                    <span key={tag} className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-800 text-xs">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <h2 className="mt-10 flex items-center gap-2 font-heading text-xl font-bold text-foreground-900">
              <span className="w-1.5 h-5 rounded-full bg-primary-500"></span>
              医师简介
            </h2>
            <p className="mt-4 text-sm md:text-base text-foreground-700 leading-relaxed">{doctor.intro}</p>

            <h2 className="mt-10 flex items-center gap-2 font-heading text-xl font-bold text-foreground-900">
              <span className="w-1.5 h-5 rounded-full bg-accent-500"></span>
              擅长领域
            </h2>
            <div className="mt-4 flex flex-wrap gap-2">
              {doctor.tags.map((item) => (
                <span key={item} className="px-3.5 py-2 rounded-full bg-accent-50 border border-accent-100 text-accent-800 text-sm">
                  {item}
                </span>
              ))}
            </div>

            {doctorCourses.length > 0 && (
              <>
                <h2 className="mt-10 flex items-center gap-2 font-heading text-xl font-bold text-foreground-900">
                  <span className="w-1.5 h-5 rounded-full bg-secondary-500"></span>
                  主讲课程
                </h2>
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {doctorCourses.map((course) => (
                    <Link
                      key={course.id}
                      to={`/courses/${course.id}`}
                      className="group flex gap-4 p-4 rounded-lg border border-background-200 hover:border-primary-300 transition-colors cursor-pointer"
                    >
                      <div className="w-24 h-20 rounded-md overflow-hidden shrink-0">
                        <img
                          src={course.image}
                          alt={course.title}
                          title={`${course.title} - 精品课程`}
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-heading font-medium text-sm text-foreground-900 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
                          {course.title}
                        </h3>
                        <span className="mt-2 inline-flex items-center gap-1 text-xs text-primary-600">
                          <i className="ri-play-list-line"></i>
                          {course.lessons} 课时
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              </>
            )}
          </div>

          <aside className="lg:col-span-1">
            <div className="lg:sticky lg:top-24 rounded-lg border border-background-200 bg-background-50 p-6">
              <h3 className="font-heading font-semibold text-foreground-900 mb-4">医师信息</h3>
              <div className="space-y-3 text-sm text-foreground-700">
                <div className="flex items-center justify-between">
                  <span className="text-foreground-500">所在医院</span>
                  <span className="font-medium text-foreground-900 text-right">{doctor.hospital}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-foreground-500">职称</span>
                  <span className="font-medium text-foreground-900">{doctor.title.split(' · ')[0]}</span>
                </div>
              </div>

              {doctorVideos.length > 0 && (
                <div className="mt-6 pt-6 border-t border-background-200">
                  <h4 className="font-heading font-semibold text-foreground-900 mb-3">相关视频</h4>
                  <div className="flex flex-col gap-3">
                    {doctorVideos.map((video) => (
                      <Link
                        key={video.id}
                        to={`/videos/${video.id}`}
                        className="group flex gap-3 cursor-pointer"
                      >
                        <div className="relative w-20 h-14 rounded-md overflow-hidden shrink-0">
                          <img
                            src={video.image}
                            alt={video.title}
                            title={`${video.title} - 视频专区`}
                            className="w-full h-full object-cover object-top"
                          />
                        </div>
                        <h5 className="text-xs text-foreground-700 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
                          {video.title}
                        </h5>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </aside>
        </div>
      </section>
    </PageShell>
  );
}